import type { DataTableRow } from "@client-shared";
import type { Row } from "@tanstack/react-table";

/** Get all selectable (non-disabled) leaf rows from a row tree, recursing into grouped rows */
export const getAllSelectableLeafRows = <T extends object>(row: Row<DataTableRow<T>>): Row<DataTableRow<T>>[] => {
  if (row.getIsGrouped() && row.subRows) {
    const leafRows: Row<DataTableRow<T>>[] = [];
    for (const subRow of row.subRows) {
      leafRows.push(...getAllSelectableLeafRows(subRow));
    }
    return leafRows;
  }
  return row.original.isDisabled ? [] : [row];
};

/** Mark group rows as selected when all their selectable descendant leaf rows are selected.
 *  Recurses into nested groups and returns true if the group was marked selected. */
export const markGroupAsSelected = <T extends object>(row: Row<DataTableRow<T>>, newSelection: Record<string, boolean>): boolean => {
  if (!row.getIsGrouped() || !row.subRows || row.subRows.length === 0) {
    return false;
  }

  const selectableChildren: Row<DataTableRow<T>>[] = [];
  for (const subRow of row.subRows) {
    if (subRow.getIsGrouped()) {
      markGroupAsSelected(subRow, newSelection);
      const hasSelectableLeaves = getAllSelectableLeafRows(subRow).length > 0;
      if (hasSelectableLeaves) {
        selectableChildren.push(subRow);
      }
      continue;
    }
    if (!subRow.original.isDisabled) {
      selectableChildren.push(subRow);
    }
  }

  const allSelectableChildrenSelected =
    selectableChildren.length > 0 && selectableChildren.every((subRow) => newSelection[subRow.id] === true);

  if (allSelectableChildrenSelected) {
    newSelection[row.id] = true;
    return true;
  }

  return false;
};
